import axios from 'axios';
import {WebFormModel} from "capitalist-shared/src/core/model";
import { RemoteRequestState } from '@/backoffice/components/pages/taxrules/taxrules';
import {CurrencyCode, isCardsProAccountNumber} from '@/models/core/currency';
import httpAxiosApi from "capitalist-shared/src/core/axios.errors.handle";

export interface CryptoAddressInterface {
    address: string;
    name: string;
    currency: CurrencyCode;
    is_generating: boolean;
    deprecated: boolean;
    id: number;
    cardDepositUrl: string | null;
    system: string | null;
    hidden: boolean;
    autoConvertAccountNumber: string | null;
    autoConvert: boolean;
}

export class CryptoAddress implements CryptoAddressInterface, Record<string, any> {
    address = '';

    currency: CurrencyCode = CurrencyCode.USD;

    autoConvertAccountNumber = '';

    autoConvert = false;

    name = '';

    is_generating = false;

    deprecated = false;

    id = 0;

    cardDepositUrl = null;

    system = '';

    hidden = false;

    static createFromData(data = {}): CryptoAddress {
      return Object.assign(new CryptoAddress(), {...data});
    }

    get censoredAddress(): string {
      if (this.address.length == 0) return '';
      return this.address.replace(/^(.{8}).+(.{8})$/, '$1...$2');
    }

    get publicCurrency(): string {
      return this.currency === CurrencyCode.USDTt ? CurrencyCode.USDT : this.currency;
    }

    get normalizedName(): string {
      return [this.name,
        this.address, this.currency, this.publicCurrency,
        this.system, this.autoConvertAccountNumber,
        this.autoConvert && this.autoConvertAccountNumber ? 'autoconvert автоконверт' : null,
        this.isCardsProAutoconvertAccountNumber ? 'cardspro' : null,
      ].filter((s) => s).join(' ')
    }

    get isCardsProAutoconvertAccountNumber(): boolean {
      return this.autoConvert && this.autoConvertAccountNumber &&
        isCardsProAccountNumber(this.autoConvertAccountNumber);
    }
}

export class CryptoAddressVisibilityModel extends WebFormModel {
    state: RemoteRequestState = new RemoteRequestState();

    values = { id: '', visible: false }

    toggleVisibility(address: CryptoAddress) {
      this.state.loadStart();
      this.values.id = `${address.id}`;
      this.values.visible = address.hidden;

      return axios.post('/webapi/finance/accountspage', {
        call: 'hideCryptoAddress',
        m: this.values,
      }).then((result) => {
        this.state.loadEnd();
        return result.data.model;
      }).catch((err) => {
        this.state.loadError();
        throw err;
      });
    }
}

export class CryptoAddressFindModel extends WebFormModel {
    state: RemoteRequestState = new RemoteRequestState();

    values = { id: '' }

    findAddress(id: number) {
      this.state.loadStart();
      this.values.id = `${id}`;

      return axios.post('/webapi/finance/accountspage', {
        call: 'findCryptoAddress',
        m: this.values,
      }).then((result) => {
        this.state.loadEnd();
        return CryptoAddress.createFromData(result.data.model.options.address);
      }).catch((err) => {
        this.state.loadError();
        throw err;
      });
    }
}

export class CryptoAddressRenameModel extends WebFormModel {
    state: RemoteRequestState = new RemoteRequestState();

    values = { id: '', name: '' }

    renameAddress(id: number, name: string) {
      this.errors.clear();
      this.state.loadStart();
      this.values.id = `${id}`;
      this.values.name = `${name}`;

      return axios.post('/webapi/finance/accountspage', {
        call: 'renameCryptoAddress',
        m: this.values,
      }).then((result) => {
        this.state.loadEnd();
        return CryptoAddress.createFromData(result.data.model.options.address);
      }).catch((err) => {
        this.checkAxiosError(err);
        this.state.loadError();
        this.errors.set('name', 'Не удалось переименовать адрес.');
        throw err;
      });
    }
}

export enum UsdtNetworkType {
    Ethereum = 'ethereum',
    Tron = 'tron'
}

export const UsdtNetworkTitles = {
  [UsdtNetworkType.Ethereum]: 'ERC20',
  [UsdtNetworkType.Tron]: 'TRC20',
};

export class UsdtCryptoAddressCreateModel extends WebFormModel {
    state: RemoteRequestState = new RemoteRequestState();

    values:
        {network: UsdtNetworkType, name: string}
        = { network: UsdtNetworkType.Ethereum, name: 'My new address' }

    createAddress(network: UsdtNetworkType) {
      this.errors.clear();
      this.state.loadStart();
      this.values.network = network;
      this.values.name = 'New address';

      return axios.post('/webapi/finance/accountspage', {
        call: 'createUsdtCryptoAddress',
        m: this.values,
      }).then((result) => {
        this.state.loadEnd();
        return CryptoAddress.createFromData(result.data.model.options.address);
      }).catch((err) => {
        this.checkAxiosError(err);
        this.state.loadError();
        this.errors.set('name', 'Не удалось создать адрес.');
        throw err;
      });
    }
}

export class CryptoAddressCreateModel extends WebFormModel {
    state: RemoteRequestState = new RemoteRequestState();

    values:
        {name: string, currency: CurrencyCode}
        = { name: 'My new address', currency: CurrencyCode.BTC }

    createAddress(currency: CurrencyCode) {
        this.values.currency = currency;
        this.errors.clear();
        this.state.loadStart();
        this.values.name = 'New address';

        return httpAxiosApi.post('/webapi/finance/accounts/generateCryptoAddress', {
            call: 'generateCryptoAddress',
            m: this.values,
        }).then((result) => {
            this.state.loadEnd();
            return CryptoAddress.createFromData(result.data.wallet);
        }).catch((err) => {
            this.checkAxiosError(err);
            this.state.loadError();
            // this.errors.set('name', 'Не удалось создать адрес.');
            throw err;
        });
    }
}

export class UsdtAddressesModel extends WebFormModel {
    addresses: any[] = [];

    loadingState: RemoteRequestState = new RemoteRequestState();

    updateAddress(address: CryptoAddress) {
      for (let i = 0; i < this.addresses.length; i++) {
        if (this.addresses[i].id === address.id) {
          this.addresses.splice(i, 1, address);
          return;
        }
      }
      this.addresses.push(address);
    }

    loadAddresses(currency = CurrencyCode.USDT) {
      this.loadingState.loadStart();
      return axios.get('/webapi/finance/accountspage/watchCryptoAddresses', {
        params: {
          call: 'watchCryptoAddresses',
          currency,
        },
      }).then((result) => {
        this.loadingState.loadEnd();
        this.addresses = result.data.model.wallets.map((w) => CryptoAddress.createFromData(w));
        return result.data;
      }).catch((err) => {
        this.loadingState.loadError();
        throw err;
      });
    }
}

export class UsdcAddressesModel extends WebFormModel {
    addresses: any[] = [];

    loadingState: RemoteRequestState = new RemoteRequestState();

    updateAddress(address: CryptoAddress) {
        for (let i = 0; i < this.addresses.length; i++) {
            if (this.addresses[i].id === address.id) {
                this.addresses.splice(i, 1, address);
                return;
            }
        }
        this.addresses.push(address);
    }

    loadAddresses(currency = CurrencyCode.USDC) {
        this.loadingState.loadStart();
        return axios.get('/webapi/finance/accountspage/watchCryptoAddresses', {
            params: {
                call: 'watchCryptoAddresses',
                currency,
            },
        }).then((result) => {
            this.loadingState.loadEnd();
            this.addresses = result.data.model.wallets.map((w) => CryptoAddress.createFromData(w));
            return result.data;
        }).catch((err) => {
            this.loadingState.loadError();
            throw err;
        });
    }
}
